/**
 * PocketBase Realtime Adapter
 *
 * Provides real-time bid updates via PocketBase WebSocket subscriptions.
 * Replaces polling with instant updates for a better user experience.
 */

import PocketBase, { RecordSubscription, RecordModel } from 'pocketbase';

export interface RealtimeAdapter {
  subscribe: (channel: string, callback: (data: unknown) => void) => () => void;
}

export interface RealtimeConnectionState {
  isConnected: boolean;
  lastError: Error | null;
}

/**
 * Creates a realtime adapter for PocketBase
 *
 * @param pb - PocketBase client instance
 * @returns RealtimeAdapter compatible with @boli/core
 */
export function createPocketBaseRealtimeAdapter(pb: PocketBase): RealtimeAdapter {
  // Track active subscriptions for cleanup
  const activeSubscriptions = new Map<string, () => void>();

  return {
    subscribe: (channel: string, callback: (data: unknown) => void) => {
      // Parse channel format: "auctions/{auctionId}/bids"
      const bidsMatch = channel.match(/^auctions\/(.+)\/bids$/);

      if (bidsMatch) {
        const auctionId = bidsMatch[1];

        // Subscribe to all bid changes for this auction
        const subscriptionKey = `bids_${auctionId}`;

        // Avoid duplicate subscriptions
        if (activeSubscriptions.has(subscriptionKey)) {
          return activeSubscriptions.get(subscriptionKey)!;
        }

        let unsubscribeFunc: (() => void) | null = null;

        // Subscribe to the bids collection
        pb.collection('bids').subscribe('*', (e: RecordSubscription<RecordModel>) => {
          // Filter for bids in this auction
          if (e.record.auctionId === auctionId) {
            // Transform PocketBase event to Boli event format
            const boliEvent = {
              type: 'BID_PLACED',
              payload: {
                bid: {
                  id: e.record.id,
                  auctionId: e.record.auctionId,
                  bidderId: e.record.bidderId,
                  amount: e.record.amount,
                  serverReceivedAt: e.record.serverReceivedAt,
                  status: e.record.status || 'confirmed',
                },
                auction: {
                  currentHighBid: e.record.amount,
                  currentHighBidderId: e.record.bidderId,
                  // These will be updated from the auction record
                  endTimestamp: 0,
                  extensionCount: 0,
                },
              },
            };

            // Also fetch the updated auction to get accurate end time
            pb.collection('auctions').getOne(auctionId).then((auction) => {
              boliEvent.payload.auction = {
                currentHighBid: auction.currentHighBid,
                currentHighBidderId: auction.currentHighBidderId,
                endTimestamp: auction.endTimestamp,
                extensionCount: auction.extensionCount || 0,
              };
              callback(boliEvent);
            }).catch(() => {
              // If auction fetch fails, still send the bid update
              callback(boliEvent);
            });
          }
        }).then((unsub) => {
          unsubscribeFunc = unsub;
          console.log(`[Realtime] Subscribed to bids for auction ${auctionId}`);
        }).catch((err) => {
          console.error('[Realtime] Failed to subscribe to bids:', err);
        });

        // Return unsubscribe function
        const cleanup = () => {
          if (unsubscribeFunc) {
            unsubscribeFunc();
          } else {
            // If subscription is still pending, try to unsubscribe from collection
            pb.collection('bids').unsubscribe('*').catch(() => {});
          }
          activeSubscriptions.delete(subscriptionKey);
          console.log(`[Realtime] Unsubscribed from bids for auction ${auctionId}`);
        };

        activeSubscriptions.set(subscriptionKey, cleanup);
        return cleanup;
      }

      // Handle auction updates channel
      const auctionMatch = channel.match(/^auctions\/(.+)$/);
      if (auctionMatch) {
        const auctionId = auctionMatch[1];
        const subscriptionKey = `auction_${auctionId}`;

        if (activeSubscriptions.has(subscriptionKey)) {
          return activeSubscriptions.get(subscriptionKey)!;
        }

        let unsubscribeFunc: (() => void) | null = null;

        pb.collection('auctions').subscribe(auctionId, (e: RecordSubscription<RecordModel>) => {
          callback({
            type: 'AUCTION_UPDATED',
            payload: {
              auction: e.record,
            },
          });
        }).then((unsub) => {
          unsubscribeFunc = unsub;
          console.log(`[Realtime] Subscribed to auction ${auctionId}`);
        }).catch((err) => {
          console.error('[Realtime] Failed to subscribe to auction:', err);
        });

        const cleanup = () => {
          if (unsubscribeFunc) {
            unsubscribeFunc();
          } else {
            pb.collection('auctions').unsubscribe(auctionId).catch(() => {});
          }
          activeSubscriptions.delete(subscriptionKey);
          console.log(`[Realtime] Unsubscribed from auction ${auctionId}`);
        };

        activeSubscriptions.set(subscriptionKey, cleanup);
        return cleanup;
      }

      // Unknown channel - return no-op
      console.warn(`[Realtime] Unknown channel format: ${channel}`);
      return () => {};
    },
  };
}

/**
 * Creates a PocketBase client with realtime capabilities
 *
 * @param apiBaseUrl - The PocketBase API URL
 * @returns Object with pb instance and realtime adapter
 */
export function createRealtimeClient(apiBaseUrl: string) {
  const pb = new PocketBase(apiBaseUrl);

  // Enable auto-cancellation for better performance
  pb.autoCancellation(false);

  return {
    pb,
    realtimeAdapter: createPocketBaseRealtimeAdapter(pb),
  };
}
